import {Statistics, statisticsType, StatisticsType, StatisticsWrapper} from "./statistics";

import {calculateAverage} from "../../util/math-util";
import {transportCategory, TransportCategory} from "../transport-category";
import {Exclude, Expose} from "class-transformer";
import {transportMethod} from "../transport-method";

export interface TruckingCategoryStatistics extends Statistics {
  totalLoads: number
  totalRejects: number
  havingTravelTime: number
  totalTravelTime: number
  havingOnSiteTime: number
  totalOnSiteTime: number
  havingTurnaroundTime: number
  totalTurnaroundTime: number
  rejectsByType: Record<string, number>
  deliveredToNonPrimarySiteCount: number
}

@Exclude()
export class TruckingCategoryStatisticsWrapper extends StatisticsWrapper implements TruckingCategoryStatistics {
  category: TransportCategory | undefined

  constructor(category: TransportCategory | undefined) {
    super();

    this.category = category
  }

  @Expose()
  totalLoads = 0;

  @Expose()
  totalRejects = 0;

  @Expose()
  havingTravelTime = 0;

  @Expose()
  totalTravelTime = 0;

  @Expose()
  havingOnSiteTime = 0;

  @Expose()
  totalOnSiteTime = 0;

  @Expose()
  avgDailyOnSiteTime = 0;

  @Expose()
  havingTurnaroundTime = 0;

  @Expose()
  totalTurnaroundTime = 0;

  @Expose()
  rejectsByType: Record<string, number> = {}

  @Expose()
  deliveredToNonPrimarySiteCount = 0

  protected getCategoryStats(category: TransportCategory): StatisticsWrapper | undefined {
    if (this.category !== category) {
      return undefined
    }

    return this
  }

  protected getCategoryStatsList(category: TransportCategory | undefined = undefined): StatisticsWrapper[] {
    const categoryStatsList: StatisticsWrapper[] = []

    if (category) {
      const categoryStats = this.getCategoryStats(category)
      if (categoryStats) {
        categoryStatsList.push(categoryStats)
      }
    } else {
      categoryStatsList.push(this)
    }

    return categoryStatsList
  }

  get avgTravelTime(): number {
    return calculateAverage(this.totalTravelTime, this.havingTravelTime);
  }

  get avgOnSiteTime(): number {
    return calculateAverage(this.totalOnSiteTime, this.havingOnSiteTime);
  }

  get avgTurnaroundTime(): number {
    return calculateAverage(
      this.totalTurnaroundTime,
      this.havingTurnaroundTime
    );
  }

  getValue(type: StatisticsType, rateModel: string | undefined = undefined): number {
    let value = 0;

    if (!type.method || type.method === transportMethod.trucking) {
      if (type.cost && rateModel) {
        switch (type) {
          case statisticsType.cost:
            value = this.getTotalCost(rateModel)
            break;
          case statisticsType.billableTime:
            value = this.getBillableHours(rateModel)
            break;
          case statisticsType.billableQuantity:
            value = this.getBillableQty(rateModel)
            break;
          case statisticsType.timeCost:
            value = this.getTimeCost(rateModel)
            break;
          case statisticsType.genericTimeCost:
            value = this.getGenericTimeCost(rateModel)
            break;
          case statisticsType.siteRestrictionTimeCost:
            value = this.getSiteRestrictionTimeCost(rateModel)
            break;
          case statisticsType.demurrageTimeCost:
            value = this.getDemurrageTimeCost(rateModel)
            break;
          case statisticsType.pickupDemurrageTimeCost:
            value = this.getPickupDemurrageTimeCost(rateModel)
            break;
          case statisticsType.deliveryDemurrageTimeCost:
            value = this.getDeliveryDemurrageTimeCost(rateModel)
            break;
          case statisticsType.freightCost:
            value = this.getFreightCost(rateModel)
            break;
          case statisticsType.genericFreightCost:
            value = this.getGenericFreightCost(rateModel)
            break;
          case statisticsType.siteRestrictionFreightCost:
            value = this.getSiteRestrictionFreightCost(rateModel)
            break;
          case statisticsType.loadCost:
            value = this.getLoadCost(rateModel)
            break;
          case statisticsType.genericLoadCost:
            value = this.getGenericLoadCost(rateModel)
            break;
          case statisticsType.siteRestrictionLoadCost:
            value = this.getSiteRestrictionLoadCost(rateModel)
            break;
          case statisticsType.baseFuelSurchargeLoadCost:
            value = this.getBaseFuelSurchargeLoadCost(rateModel)
            break;
          case statisticsType.percentFuelSurchargeLoadCost:
            value = this.getPercentFuelSurchargeLoadCost(rateModel)
            break;
          case statisticsType.shiftLoadCost:
            value = this.getShiftLoadCost(rateModel)
            break;
          case statisticsType.ticketRejectLoadCost:
            value = this.getTicketRejectLoadCost(rateModel)
            break;
          case statisticsType.splitLoadCost:
            value = this.getSplitLoadCost(rateModel)
            break;
          case statisticsType.taxCost:
            value = this.getTaxCost(rateModel)
            break;
          case statisticsType.siteRestrictionCost:
            value = this.getSiteRestrictionCost(rateModel)
            break;
          case statisticsType.fuelSurchargeCost:
            value = this.getFuelSurchargeCost(rateModel)
            break;
          case statisticsType.costPerQuantity:
            value = this.costs[rateModel].getCostPerQty(this.totalQty)
            break;
          default:
            break;
        }
      }

      switch (type) {
        case statisticsType.loads:
          value = this.totalLoads;
          break;
        case statisticsType.rejects:
          value = this.totalRejects;
          break;
        case statisticsType.quantity:
          value = this.totalQty;
          break;
        case statisticsType.standardQuantity:
          value = this.totalStandardQty;
          break;
        case statisticsType.netQuantity:
          value = this.totalNetQty;
          break;
        case statisticsType.travelTime:
          value = this.totalTravelTime;
          break;
        case statisticsType.avgTravelTime:
          value = this.avgTravelTime;
          break;
        case statisticsType.onSiteTime:
          value = this.totalOnSiteTime;
          break;
        case statisticsType.avgOnSiteTime:
          value = this.avgOnSiteTime;
          break;
        case statisticsType.avgDailyOnSiteTime:
          value = this.avgDailyOnSiteTime;
          break;
        case statisticsType.turnaroundTime:
          value = this.totalTurnaroundTime;
          break;
        case statisticsType.avgTurnaroundTime:
          value = this.avgTurnaroundTime;
          break;
        case statisticsType.deliveredToNonPrimarySiteCount:
          value = this.deliveredToNonPrimarySiteCount
          break;
        default:
          break;
      }
    }
    return value;
  }
}

export interface TruckingStatistics {
  avgDailyOnSiteTime: number
  water: TruckingCategoryStatistics
  oil: TruckingCategoryStatistics
  other: TruckingCategoryStatistics
}

@Exclude()
export class TruckingStatisticsWrapper extends StatisticsWrapper implements TruckingStatistics {

  @Expose()
  avgDailyOnSiteTime = 0;

  @Expose()
  water = new TruckingCategoryStatisticsWrapper(transportCategory.water)

  @Expose()
  oil = new TruckingCategoryStatisticsWrapper(transportCategory.oil)

  @Expose()
  other = new TruckingCategoryStatisticsWrapper(undefined)

  protected getCategoryStats(category: TransportCategory): StatisticsWrapper | undefined {
    let truckingCategoryStats: StatisticsWrapper | undefined = undefined

    if (category === transportCategory.water) {
      truckingCategoryStats = this.water
    } else if (category === transportCategory.oil) {
      truckingCategoryStats = this.oil
    }

    return truckingCategoryStats
  }

  protected getCategoryStatsList(category: TransportCategory | undefined = undefined): StatisticsWrapper[] {
    const categoryStatsList: StatisticsWrapper[] = []

    if (category) {
      const categoryStats = this.getCategoryStats(category)
      if (categoryStats) {
        categoryStatsList.push(categoryStats)
      }
    } else {
      categoryStatsList.push(this.water, this.oil, this.other)
    }

    return categoryStatsList
  }

  getRejectsByType(category: TransportCategory | undefined = undefined): Record<string, number> {
    const categoryStatsList: TruckingCategoryStatisticsWrapper[] = this.getCategoryStatsList(category) as TruckingCategoryStatisticsWrapper[]
    return this.combine(categoryStatsList, (stats) => stats.rejectsByType)
  }

  getTotalQty(category: TransportCategory | undefined = undefined): number {
    const categoryStatsList: TruckingCategoryStatisticsWrapper[] = this.getCategoryStatsList(category) as TruckingCategoryStatisticsWrapper[]
    return this.sum(categoryStatsList, (stats) => stats.totalQty)
  }

  getTotalNetQty(category: TransportCategory | undefined = undefined): number {
    const categoryStatsList: TruckingCategoryStatisticsWrapper[] = this.getCategoryStatsList(category) as TruckingCategoryStatisticsWrapper[]
    return this.sum(categoryStatsList, (stats) => stats.totalNetQty)
  }

  getTotalStandardQty(category: TransportCategory | undefined = undefined): number {
    const categoryStatsList: TruckingCategoryStatisticsWrapper[] = this.getCategoryStatsList(category) as TruckingCategoryStatisticsWrapper[]
    return this.sum(categoryStatsList, (stats) => stats.totalStandardQty)
  }

  getTotalLoads(category: TransportCategory | undefined = undefined): number {
    const categoryStatsList: TruckingCategoryStatisticsWrapper[] = this.getCategoryStatsList(category) as TruckingCategoryStatisticsWrapper[]
    return this.sum(categoryStatsList, (stats) => stats.totalLoads)
  }

  getTotalRejects(category: TransportCategory | undefined = undefined): number {
    const categoryStatsList: TruckingCategoryStatisticsWrapper[] = this.getCategoryStatsList(category) as TruckingCategoryStatisticsWrapper[]
    return this.sum(categoryStatsList, (stats) => stats.totalRejects)
  }

  getHavingTravelTime(category: TransportCategory | undefined = undefined): number {
    const categoryStatsList: TruckingCategoryStatisticsWrapper[] = this.getCategoryStatsList(category) as TruckingCategoryStatisticsWrapper[]
    return this.sum(categoryStatsList, (stats) => stats.havingTravelTime)
  }

  getTotalTravelTime(category: TransportCategory | undefined = undefined): number {
    const categoryStatsList: TruckingCategoryStatisticsWrapper[] = this.getCategoryStatsList(category) as TruckingCategoryStatisticsWrapper[]
    return this.sum(categoryStatsList, (stats) => stats.totalTravelTime)
  }

  getHavingOnSiteTime(category: TransportCategory | undefined = undefined): number {
    const categoryStatsList: TruckingCategoryStatisticsWrapper[] = this.getCategoryStatsList(category) as TruckingCategoryStatisticsWrapper[]
    return this.sum(categoryStatsList, (stats) => stats.havingOnSiteTime)
  }

  getTotalOnSiteTime(category: TransportCategory | undefined = undefined): number {
    const categoryStatsList: TruckingCategoryStatisticsWrapper[] = this.getCategoryStatsList(category) as TruckingCategoryStatisticsWrapper[]
    return this.sum(categoryStatsList, (stats) => stats.totalOnSiteTime)
  }

  getHavingTurnaroundTime(category: TransportCategory | undefined = undefined): number {
    const categoryStatsList: TruckingCategoryStatisticsWrapper[] = this.getCategoryStatsList(category) as TruckingCategoryStatisticsWrapper[]
    return this.sum(categoryStatsList, (stats) => stats.havingTurnaroundTime)
  }

  getTotalTurnaroundTime(category: TransportCategory | undefined = undefined): number {
    const categoryStatsList: TruckingCategoryStatisticsWrapper[] = this.getCategoryStatsList(category) as TruckingCategoryStatisticsWrapper[]
    return this.sum(categoryStatsList, (stats) => stats.totalTurnaroundTime)
  }

  getAvgTravelTime(category: TransportCategory | undefined = undefined): number {
    return calculateAverage(this.getTotalTravelTime(category), this.getHavingTravelTime(category));
  }

  getAvgOnSiteTime(category: TransportCategory | undefined = undefined): number {
    return calculateAverage(this.getTotalOnSiteTime(category), this.getHavingOnSiteTime(category));
  }

  getAvgTurnaroundTime(category: TransportCategory | undefined = undefined): number {
    return calculateAverage(this.getTotalTurnaroundTime(category), this.getHavingTurnaroundTime(category));
  }

  getDeliveredToNonPrimarySiteCount(category: TransportCategory | undefined = undefined): number {
    const categoryStatsList: TruckingCategoryStatisticsWrapper[] = this.getCategoryStatsList(category) as TruckingCategoryStatisticsWrapper[]
    return this.sum(categoryStatsList, (stats) => stats.deliveredToNonPrimarySiteCount)
  }

  getCostPerQty(rateModel: string, category: TransportCategory | undefined = undefined): number {
    const totalCost = this.getTotalCost(rateModel, category)
    return calculateAverage(totalCost, this.getTotalQty(category))
  }

  getValue(type: StatisticsType, rateModel: string | undefined = undefined, category: TransportCategory | undefined = undefined): number {
    let value = 0;

    if (!type.method || type.method === transportMethod.trucking) {
      if (type.cost && rateModel) {
        switch (type) {
          case statisticsType.cost:
            value = this.getTotalCost(rateModel, category)
            break;
          case statisticsType.billableTime:
            value = this.getBillableHours(rateModel, category)
            break;
          case statisticsType.billableQuantity:
            value = this.getBillableQty(rateModel, category)
            break;
          case statisticsType.timeCost:
            value = this.getTimeCost(rateModel, category)
            break;
          case statisticsType.genericTimeCost:
            value = this.getGenericTimeCost(rateModel, category)
            break;
          case statisticsType.siteRestrictionTimeCost:
            value = this.getSiteRestrictionTimeCost(rateModel, category)
            break;
          case statisticsType.demurrageTimeCost:
            value = this.getDemurrageTimeCost(rateModel, category)
            break;
          case statisticsType.pickupDemurrageTimeCost:
            value = this.getPickupDemurrageTimeCost(rateModel, category)
            break;
          case statisticsType.deliveryDemurrageTimeCost:
            value = this.getDeliveryDemurrageTimeCost(rateModel, category)
            break;
          case statisticsType.freightCost:
            value = this.getFreightCost(rateModel, category)
            break;
          case statisticsType.genericFreightCost:
            value = this.getGenericFreightCost(rateModel, category)
            break;
          case statisticsType.siteRestrictionFreightCost:
            value = this.getSiteRestrictionFreightCost(rateModel, category)
            break;
          case statisticsType.loadCost:
            value = this.getLoadCost(rateModel, category)
            break;
          case statisticsType.genericLoadCost:
            value = this.getGenericLoadCost(rateModel, category)
            break;
          case statisticsType.siteRestrictionLoadCost:
            value = this.getSiteRestrictionLoadCost(rateModel, category)
            break;
          case statisticsType.baseFuelSurchargeLoadCost:
            value = this.getBaseFuelSurchargeLoadCost(rateModel, category)
            break;
          case statisticsType.percentFuelSurchargeLoadCost:
            value = this.getPercentFuelSurchargeLoadCost(rateModel, category)
            break;
          case statisticsType.shiftLoadCost:
            value = this.getShiftLoadCost(rateModel, category)
            break;
          case statisticsType.ticketRejectLoadCost:
            value = this.getTicketRejectLoadCost(rateModel, category)
            break;
          case statisticsType.splitLoadCost:
            value = this.getSplitLoadCost(rateModel)
            break;
          case statisticsType.taxCost:
            value = this.getTaxCost(rateModel, category)
            break;
          case statisticsType.siteRestrictionCost:
            value = this.getSiteRestrictionCost(rateModel, category)
            break;
          case statisticsType.fuelSurchargeCost:
            value = this.getFuelSurchargeCost(rateModel, category)
            break;
          case statisticsType.costPerQuantity:
            value = this.getCostPerQty(rateModel, category)
            break;
          default:
            break;
        }
      }

      switch (type) {
        case statisticsType.loads:
          value = this.getTotalLoads(category);
          break;
        case statisticsType.rejects:
          value = this.getTotalRejects(category);
          break;
        case statisticsType.quantity:
          value = this.getTotalQty(category)
          break;
        case statisticsType.netQuantity:
          value = this.getTotalNetQty(category)
          break;
        case statisticsType.standardQuantity:
          value = this.getTotalStandardQty(category)
          break;
        case statisticsType.travelTime:
          value = this.getTotalTravelTime(category)
          break;
        case statisticsType.avgTravelTime:
          value = this.getAvgTravelTime(category)
          break;
        case statisticsType.onSiteTime:
          value = this.getTotalOnSiteTime(category);
          break;
        case statisticsType.avgOnSiteTime:
          value = this.getAvgOnSiteTime(category);
          break;
        case statisticsType.avgDailyOnSiteTime:
          value = this.avgDailyOnSiteTime;
          break;
        case statisticsType.turnaroundTime:
          value = this.getTotalTurnaroundTime(category);
          break;
        case statisticsType.avgTurnaroundTime:
          value = this.getAvgTurnaroundTime(category);
          break;
        case statisticsType.deliveredToNonPrimarySiteCount:
          value = this.getDeliveredToNonPrimarySiteCount(category);
          break;
        default:
          break;
      }
    }
    return value;
  }
}
